import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Mail,
  Key,
  Building,
  Briefcase,
  Calendar as CalendarIcon,
  Shield,
  Lock,
  Send,
  Phone,
  MapPin,
  CreditCard,
  Cake,
  Users
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Employees = () => {
  const { isAdmin, isGestor } = useAuth();
  const { t, language } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [sendCredentialsDialog, setSendCredentialsDialog] = useState(false);
  const [selectedEmployeeForCredentials, setSelectedEmployeeForCredentials] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    pin: '',
    department: '',
    position: '',
    payroll_code: '',
    hire_date: '',
    termination_date: '',
    password: '',
    role: 'worker',
    is_active: true,
    dni: '',
    phone: '',
    address: '',
    birth_date: '',
    supervised_departments: [],
    supervised_employees: [],
    calendar_id: ''
  });
  const [supervisorEmpSearch, setSupervisorEmpSearch] = useState('');

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchCalendars();
  }, []);

  const fetchCalendars = async () => {
    try {
      const response = await axios.get(`${API_URL}/calendars`);
      setCalendars(response.data || []);
    } catch (error) {
      console.error('Error fetching calendars:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/departments`);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        // Update - only send changed fields
        const updateData = {};
        if (formData.name !== editingEmployee.name) updateData.name = formData.name;
        if (formData.email !== editingEmployee.email) updateData.email = formData.email;
        if (formData.pin !== editingEmployee.pin) updateData.pin = formData.pin;
        if (formData.department !== (editingEmployee.department || '')) updateData.department = formData.department;
        if (formData.position !== (editingEmployee.position || '')) updateData.position = formData.position;
        if (formData.payroll_code !== (editingEmployee.payroll_code || '')) updateData.payroll_code = formData.payroll_code;
        if (formData.hire_date !== (editingEmployee.hire_date || '')) updateData.hire_date = formData.hire_date;
        if (formData.termination_date !== (editingEmployee.termination_date || '')) updateData.termination_date = formData.termination_date;
        if (formData.role !== (editingEmployee.role || 'worker')) updateData.role = formData.role;
        if (formData.is_active !== editingEmployee.is_active) updateData.is_active = formData.is_active;
        // Supervisor restrictions (always include when role is supervisor)
        if (formData.role === 'supervisor' || editingEmployee.role === 'supervisor') {
          updateData.supervised_departments = formData.supervised_departments;
          updateData.supervised_employees = formData.supervised_employees;
        }
        // New optional fields
        if (formData.dni !== (editingEmployee.dni || '')) updateData.dni = formData.dni;
        if (formData.phone !== (editingEmployee.phone || '')) updateData.phone = formData.phone;
        if (formData.address !== (editingEmployee.address || '')) updateData.address = formData.address;
        if (formData.birth_date !== (editingEmployee.birth_date || '')) updateData.birth_date = formData.birth_date;
        if (formData.calendar_id !== (editingEmployee.calendar_id || '')) updateData.calendar_id = formData.calendar_id || null;

        // Handle password change
        if (formData.password && formData.password.trim() !== '') {
          updateData.new_password = formData.password;
        }
        
        await axios.put(`${API_URL}/employees/${editingEmployee.id}`, updateData);
      } else {
        // Create new employee
        const createData = {
          name: formData.name,
          email: formData.email,
          pin: formData.pin,
          department: formData.department || undefined,
          position: formData.position || undefined,
          payroll_code: formData.payroll_code || undefined,
          hire_date: formData.hire_date || undefined,
          termination_date: formData.termination_date || undefined,
          role: formData.role,
          password: formData.password || undefined,
          dni: formData.dni || undefined,
          phone: formData.phone || undefined,
          address: formData.address || undefined,
          birth_date: formData.birth_date || undefined,
          supervised_departments: formData.role === 'supervisor' ? formData.supervised_departments : undefined,
          supervised_employees: formData.role === 'supervisor' ? formData.supervised_employees : undefined,
          calendar_id: formData.calendar_id || undefined
        };
        await axios.post(`${API_URL}/employees`, createData);
      }
      fetchEmployees();
      handleCloseDialog();
    } catch (error) {
      alert(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleOpenCreate = () => {
    setEditingEmployee(null);
    setSupervisorEmpSearch('');
    setFormData({
      name: '',
      email: '',
      pin: '',
      department: '',
      position: '',
      payroll_code: '',
      hire_date: '',
      termination_date: '',
      password: '',
      role: 'worker',
      is_active: true,
      dni: '',
      phone: '',
      address: '',
      birth_date: '',
      supervised_departments: [],
      supervised_employees: [],
      calendar_id: ''
    });
    setDialogOpen(true);
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setSupervisorEmpSearch('');
    setFormData({
      name: employee.name,
      email: employee.email,
      pin: employee.pin,
      department: employee.department || '',
      position: employee.position || '',
      payroll_code: employee.payroll_code || '',
      hire_date: employee.hire_date || '',
      termination_date: employee.termination_date || '',
      password: '',
      role: employee.role || 'worker',
      is_active: employee.is_active !== false,
      dni: employee.dni || '',
      phone: employee.phone || '',
      address: employee.address || '',
      birth_date: employee.birth_date || '',
      supervised_departments: employee.supervised_departments || [],
      supervised_employees: employee.supervised_employees || [],
      calendar_id: employee.calendar_id || ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEmployee(null);
    setSupervisorEmpSearch('');
    setFormData({
      name: '',
      email: '',
      pin: '',
      department: '',
      position: '',
      payroll_code: '',
      hire_date: '',
      termination_date: '',
      password: '',
      role: 'worker',
      is_active: true,
      dni: '',
      phone: '',
      address: '',
      birth_date: '',
      supervised_departments: [],
      supervised_employees: [],
      calendar_id: ''
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete'))) {
      try {
        await axios.delete(`${API_URL}/employees/${id}`);
        fetchEmployees();
        toast.success(language === 'es' ? 'Empleado eliminado' : 'Employee deleted');
      } catch (error) {
        toast.error(error.response?.data?.detail || t('error_occurred'));
      }
    }
  };

  const handleOpenSendCredentials = (employee) => {
    setSelectedEmployeeForCredentials(employee);
    setTempPassword(generateRandomPassword());
    setSendCredentialsDialog(true);
  };

  const generateRandomPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleSendCredentials = async () => {
    if (!tempPassword.trim()) {
      toast.error(language === 'es' ? 'Ingrese una contraseña' : 'Enter a password');
      return;
    }
    
    setSendingCredentials(true);
    try {
      await axios.post(`${API_URL}/employees/${selectedEmployeeForCredentials.id}/send-credentials?temp_password=${encodeURIComponent(tempPassword)}`);
      toast.success(language === 'es' ? 'Credenciales enviadas correctamente' : 'Credentials sent successfully');
      setSendCredentialsDialog(false);
      setSelectedEmployeeForCredentials(null);
      setTempPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al enviar credenciales. ¿Está configurado el servidor SMTP?' : 'Error sending credentials. Is SMTP server configured?'));
    } finally {
      setSendingCredentials(false);
    }
  };

  const filteredEmployees = employees.filter(emp => 
    emp.name.toLowerCase().includes(search.toLowerCase()) ||
    emp.email.toLowerCase().includes(search.toLowerCase()) ||
    emp.department?.toLowerCase().includes(search.toLowerCase())
  );

  const generatePin = () => {
    const pin = Math.floor(1000 + Math.random() * 9000).toString();
    setFormData(prev => ({ ...prev, pin }));
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-800',
      supervisor: 'bg-blue-100 text-blue-800',
      worker: 'bg-slate-100 text-slate-800',
      inspector: 'bg-amber-100 text-amber-800'
    };
    return styles[role] || styles.worker;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  // Gestor (external payroll manager) only needs to match their own software's
  // employee list against ours - name and payroll code, nothing else. The
  // backend already trims the response to these fields for this role.
  if (isGestor && isGestor()) {
    return (
      <div className="space-y-6" data-testid="employees-page-gestor">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{t('employees')}</h1>
          <p className="text-slate-500">
            {language === 'es' ? `${employees.length} empleados` : `${employees.length} employees`}
          </p>
        </div>
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{language === 'es' ? 'Nombre' : 'Name'}</TableHead>
                  <TableHead>{language === 'es' ? 'Código de nómina' : 'Payroll code'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.filter(e => e.is_active !== false).map((emp) => (
                  <TableRow key={emp.id}>
                    <TableCell>{emp.name}</TableCell>
                    <TableCell>{emp.payroll_code || <span className="text-slate-400 italic">{language === 'es' ? 'sin asignar' : 'not set'}</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{t('employees')}</h1>
          <p className="text-slate-500">
            {language === 'es' ? `${employees.length} empleados registrados` : `${employees.length} registered employees`}
          </p>
        </div>
        {isAdmin() && (
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700" 
            onClick={handleOpenCreate}
            data-testid="add-employee-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('add_employee')}
          </Button>
        )}
      </div>

      {/* Dialog for Create/Edit */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) handleCloseDialog(); else setDialogOpen(open); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingEmployee 
                ? (language === 'es' ? 'Editar Empleado' : 'Edit Employee')
                : t('add_employee')
              }
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>{t('name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="employee-name-input"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>{t('email')} *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="pl-10"
                    required
                    data-testid="employee-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('pin')} *</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={formData.pin}
                      onChange={(e) => setFormData(prev => ({ ...prev, pin: e.target.value }))}
                      className="pl-10 font-mono"
                      maxLength={6}
                      required
                      data-testid="employee-pin-input"
                    />
                  </div>
                  <Button type="button" variant="outline" onClick={generatePin}>
                    {language === 'es' ? 'Gen.' : 'Gen.'}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Rol' : 'Role'} *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                  <SelectTrigger data-testid="employee-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-600" />
                        {t('admin')}
                      </div>
                    </SelectItem>
                    <SelectItem value="supervisor">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-600" />
                        {t('supervisor')}
                      </div>
                    </SelectItem>
                    <SelectItem value="worker">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-slate-600" />
                        {t('worker')}
                      </div>
                    </SelectItem>
                    <SelectItem value="inspector">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-amber-600" />
                        {t('inspector')}
                      </div>
                    </SelectItem>
                    <SelectItem value="gestor">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-teal-600" />
                        {language === 'es' ? 'Gestor (nómina)' : 'Manager (payroll)'}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('department')}</Label>
                {departments.length > 0 ? (
                  <Select
                    value={formData.department || "none"}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, department: value === "none" ? "" : value }))}
                  >
                    <SelectTrigger data-testid="employee-department-select">
                      <SelectValue placeholder={language === 'es' ? 'Seleccionar departamento' : 'Select department'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{language === 'es' ? 'Sin departamento' : 'No department'}</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={formData.department}
                      onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                      className="pl-10"
                      placeholder={language === 'es' ? 'Configura departamentos en Ajustes' : 'Configure departments in Settings'}
                      data-testid="employee-department-input"
                    />
                  </div>
                )}
              </div>
              {calendars.length > 0 && (
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Calendario laboral' : 'Work calendar'}</Label>
                  <Select
                    value={formData.calendar_id || 'none'}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, calendar_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'es' ? 'Calendario de la organización' : 'Organization calendar'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        {language === 'es' ? 'Usar calendario de la organización' : 'Use organization calendar'}
                      </SelectItem>
                      {calendars.map(cal => (
                        <SelectItem key={cal.id} value={cal.id}>{cal.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {language === 'es'
                      ? 'Permite asignar un calendario diferente al de la organización (festivos distintos por comunidad, turno, etc.)'
                      : 'Assign a different calendar than the organization default (different holidays by region, shift, etc.)'}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label>{t('position')}</Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={formData.position}
                    onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                    className="pl-10"
                    data-testid="employee-position-input"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Código de nómina' : 'Payroll code'}</Label>
              <Input
                value={formData.payroll_code}
                onChange={(e) => setFormData(prev => ({ ...prev, payroll_code: e.target.value }))}
                placeholder={language === 'es' ? 'Código del empleado en el programa de nómina (A3, Sage...)' : 'Employee code in the payroll software (A3, Sage...)'}
                data-testid="employee-payroll-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('hire_date')}</Label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="date"
                  value={formData.hire_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, hire_date: e.target.value }))}
                  className="pl-10"
                  data-testid="employee-hire-date-input"
                />
              </div>
            </div>
            
            {/* Optional fields section */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">
                {language === 'es' ? 'Datos adicionales (opcionales)' : 'Additional data (optional)'}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>DNI/NIE</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={formData.dni}
                      onChange={(e) => setFormData(prev => ({ ...prev, dni: e.target.value }))}
                      className="pl-10"
                      placeholder="12345678A"
                      data-testid="employee-dni-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="pl-10"
                      placeholder="+34 600 000 000"
                      data-testid="employee-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Fecha de nacimiento' : 'Birth date'}</Label>
                  <div className="relative">
                    <Cake className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="date"
                      value={formData.birth_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, birth_date: e.target.value }))}
                      className="pl-10"
                      data-testid="employee-birth-date-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Fecha de baja' : 'Termination date'}</Label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="date"
                      value={formData.termination_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, termination_date: e.target.value }))}
                      className="pl-10"
                      data-testid="employee-termination-date-input"
                    />
                  </div>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>{language === 'es' ? 'Dirección' : 'Address'}</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Textarea
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      className="pl-10 min-h-[60px]"
                      placeholder={language === 'es' ? 'Calle, número, ciudad, CP...' : 'Street, number, city, postal code...'}
                      data-testid="employee-address-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Supervisor configuration — only when role is supervisor */}
            {formData.role === 'supervisor' && (
              <div className="border-t pt-4 mt-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                  <Users className="w-4 h-4" />
                  {language === 'es' ? 'Configuración de supervisión' : 'Supervision configuration'}
                </div>
                <p className="text-xs text-slate-500">
                  {language === 'es'
                    ? 'Sin restricciones: supervisa a todos los trabajadores. Selecciona departamentos y/o empleados concretos para limitar el alcance. Los empleados asignados individualmente tienen prioridad sobre los departamentos.'
                    : 'No restrictions: supervises all workers. Select departments and/or specific employees to limit scope. Individually assigned employees take priority over departments.'}
                </p>

                {/* Departments */}
                {departments.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-slate-600">
                      {language === 'es' ? 'Departamentos supervisados' : 'Supervised departments'}
                    </Label>
                    <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1 bg-slate-50">
                      {departments.map(dept => (
                        <label key={dept} className="flex items-center gap-2 cursor-pointer text-sm py-0.5 hover:text-slate-900">
                          <input
                            type="checkbox"
                            checked={formData.supervised_departments.includes(dept)}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...formData.supervised_departments, dept]
                                : formData.supervised_departments.filter(d => d !== dept);
                              setFormData(prev => ({ ...prev, supervised_departments: updated }));
                            }}
                            className="rounded border-slate-300"
                          />
                          <span>{dept}</span>
                        </label>
                      ))}
                    </div>
                    {formData.supervised_departments.length > 0 && (
                      <p className="text-xs text-blue-600">
                        {language === 'es' ? `${formData.supervised_departments.length} departamento(s) seleccionado(s)` : `${formData.supervised_departments.length} department(s) selected`}
                      </p>
                    )}
                  </div>
                )}

                {/* Specific employees */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-slate-600">
                    {language === 'es' ? 'Empleados específicos (adicionales)' : 'Specific employees (additional)'}
                  </Label>
                  <Input
                    placeholder={language === 'es' ? 'Buscar empleado...' : 'Search employee...'}
                    value={supervisorEmpSearch}
                    onChange={(e) => setSupervisorEmpSearch(e.target.value)}
                    className="h-8 text-sm"
                    autoComplete="off"
                    name="supervisor-employee-search"
                  />
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1 bg-slate-50">
                    {employees
                      .filter(e =>
                        e.id !== (editingEmployee?.id) &&
                        e.role !== 'admin' &&
                        (!supervisorEmpSearch || e.name.toLowerCase().includes(supervisorEmpSearch.toLowerCase()))
                      )
                      .map(emp => (
                        <label key={emp.id} className="flex items-center gap-2 cursor-pointer text-sm py-0.5 hover:text-slate-900">
                          <input
                            type="checkbox"
                            checked={formData.supervised_employees.includes(emp.id)}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...formData.supervised_employees, emp.id]
                                : formData.supervised_employees.filter(id => id !== emp.id);
                              setFormData(prev => ({ ...prev, supervised_employees: updated }));
                            }}
                            className="rounded border-slate-300"
                          />
                          <span className="flex-1 truncate">{emp.name}</span>
                          {emp.department && <span className="text-xs text-slate-400 truncate">{emp.department}</span>}
                        </label>
                      ))}
                  </div>
                  {formData.supervised_employees.length > 0 && (
                    <p className="text-xs text-blue-600">
                      {language === 'es' ? `${formData.supervised_employees.length} empleado(s) asignado(s) individualmente` : `${formData.supervised_employees.length} employee(s) individually assigned`}
                    </p>
                  )}
                </div>

                {/* Summary */}
                {(formData.supervised_departments.length === 0 && formData.supervised_employees.length === 0) && (
                  <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                    {language === 'es' ? 'Sin restricciones: supervisa a todos los trabajadores activos.' : 'No restrictions: supervises all active workers.'}
                  </div>
                )}
              </div>
            )}

            {/* Password field - always shown */}
            <div className="space-y-2">
              <Label>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  {editingEmployee 
                    ? (language === 'es' ? 'Nueva Contraseña' : 'New Password')
                    : t('password')
                  }
                </div>
              </Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder={editingEmployee 
                  ? (language === 'es' ? 'Dejar vacío para no cambiar' : 'Leave empty to keep current')
                  : (language === 'es' ? 'Contraseña para acceso web' : 'Password for web access')
                }
                data-testid="employee-password-input"
              />
              <p className="text-xs text-slate-500">
                {editingEmployee 
                  ? (language === 'es' ? 'Solo rellenar si desea cambiar la contraseña actual' : 'Only fill if you want to change the current password')
                  : (language === 'es' ? 'Necesaria para acceso web. Solo PIN para kiosko.' : 'Required for web access. PIN only for kiosk.')
                }
              </p>
            </div>
            
            {editingEmployee && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label>{language === 'es' ? 'Usuario activo' : 'Active user'}</Label>
                  <p className="text-xs text-slate-500">
                    {language === 'es' ? 'Desactivar para bloquear acceso' : 'Disable to block access'}
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  data-testid="employee-active-switch"
                />
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="employee-submit-btn">
                {editingEmployee 
                  ? (language === 'es' ? 'Actualizar' : 'Update')
                  : (language === 'es' ? 'Crear Empleado' : 'Create Employee')
                }
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder={language === 'es' ? 'Buscar empleados...' : 'Search employees...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="employee-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">{t('name')}</TableHead>
                <TableHead className="font-semibold">{t('email')}</TableHead>
                <TableHead className="font-semibold">{t('department')}</TableHead>
                <TableHead className="font-semibold">{language === 'es' ? 'Rol' : 'Role'}</TableHead>
                <TableHead className="font-semibold">{t('pin')}</TableHead>
                <TableHead className="font-semibold">{t('status')}</TableHead>
                {isAdmin() && <TableHead className="font-semibold text-right">{t('actions')}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin() ? 7 : 6} className="text-center py-8 text-slate-500">
                    {t('no_results')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((employee) => (
                  <TableRow key={employee.id} className="hover:bg-slate-50" data-testid={`employee-row-${employee.id}`}>
                    <TableCell className="font-medium">{employee.name}</TableCell>
                    <TableCell className="text-slate-600">{employee.email}</TableCell>
                    <TableCell className="text-slate-600">{employee.department || '-'}</TableCell>
                    <TableCell>
                      <Badge className={getRoleBadge(employee.role)}>
                        {t(employee.role || 'worker')}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-slate-600">{employee.pin}</TableCell>
                    <TableCell>
                      <Badge variant={employee.is_active !== false ? 'default' : 'secondary'} className={employee.is_active !== false ? 'bg-emerald-100 text-emerald-800' : ''}>
                        {employee.is_active !== false ? t('active') : t('inactive')}
                      </Badge>
                    </TableCell>
                    {isAdmin() && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenSendCredentials(employee)}
                            title={language === 'es' ? 'Enviar credenciales' : 'Send credentials'}
                            data-testid={`send-credentials-${employee.id}`}
                          >
                            <Send className="w-4 h-4 text-indigo-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEdit(employee)}
                            data-testid={`edit-employee-${employee.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(employee.id)}
                            data-testid={`delete-employee-${employee.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Send Credentials Dialog */}
      <Dialog open={sendCredentialsDialog} onOpenChange={(open) => {
        if (!open) {
          setSendCredentialsDialog(false);
          setSelectedEmployeeForCredentials(null);
          setTempPassword('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {language === 'es' ? 'Enviar Credenciales' : 'Send Credentials'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                {language === 'es' 
                  ? 'Se enviará un correo electrónico con las credenciales de acceso a:'
                  : 'An email with access credentials will be sent to:'}
              </p>
              <p className="font-medium text-slate-900 mt-1">
                {selectedEmployeeForCredentials?.name} ({selectedEmployeeForCredentials?.email})
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Contraseña temporal' : 'Temporary password'}</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  placeholder={language === 'es' ? 'Contraseña temporal' : 'Temporary password'}
                  data-testid="temp-password-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setTempPassword(generateRandomPassword())}
                >
                  {language === 'es' ? 'Gen.' : 'Gen.'}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                {language === 'es' 
                  ? 'Esta contraseña será enviada al empleado junto con su PIN.'
                  : 'This password will be sent to the employee along with their PIN.'}
              </p>
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                {language === 'es' 
                  ? '⚠️ Asegúrese de que el servidor SMTP está configurado en Configuración.'
                  : '⚠️ Make sure SMTP server is configured in Settings.'}
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setSendCredentialsDialog(false);
                  setSelectedEmployeeForCredentials(null);
                  setTempPassword('');
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                type="button"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                onClick={handleSendCredentials}
                disabled={sendingCredentials || !tempPassword.trim()}
              >
                {sendingCredentials ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner" />
                    {language === 'es' ? 'Enviando...' : 'Sending...'}
                  </span>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Enviar' : 'Send'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
